// Line-item logic for the free Expense Tracker sidebar. Pure functions so the rules are testable.
// Every income or expense entry is a line item: a name, a unit choice (one-off, per day, # of units),
// an optional description, a category, and an optional user-made subheader inside that category.
// A per-day item is stored as one entry per day that shares a seriesId, like a recurring event.
import { calcMerch } from './expenseMerch';

export const EXPENSE_CATEGORIES = [
  { id: 'crew',     label: 'Crew Costs',   icon: 'people-outline' },
  { id: 'rental',   label: 'Car Rental',   icon: 'car-outline' },
  { id: 'merch',    label: 'Merch',        icon: 'shirt-outline' },
  { id: 'lodging',  label: 'Lodging',      icon: 'bed-outline' },
  { id: 'travel',   label: 'Travel',       icon: 'airplane-outline' },
  { id: 'fuel',     label: 'Fuel',         icon: 'flame-outline' },
  { id: 'other',    label: 'Other',        icon: 'ellipsis-horizontal-outline' },
];

export const INCOME_CATEGORIES = [
  { id: 'guarantee', label: 'Guarantee / Door', icon: 'ticket-outline' },
  { id: 'merch_net', label: 'Merch Net',        icon: 'shirt-outline' },
  { id: 'other_in',  label: 'Other Income',     icon: 'cash-outline' },
];

export const UNIT_MODES = [
  { id: 'one_off', label: 'One-off' },
  { id: 'per_day', label: 'Per day' },
  { id: 'units',   label: '# of units' },
];

export const SCOPES = [
  { id: 'this',   label: 'This occurrence' },
  { id: 'future', label: 'This and future occurrences' },
  { id: 'all',    label: 'All occurrences' },
];

export const MAX_SERIES_DAYS = 400;
// Firestore rejects a batch over 500 writes; stay well under it.
export const BATCH_SIZE = 400;

const num = (v) => {
  const x = parseFloat(v);
  return Number.isFinite(x) ? x : 0;
};

const pad = (n) => String(n).padStart(2, '0');

/**
 * Money as typed on any keyboard, returned with a plain "." decimal point.
 * A comma can be the decimal point ("12,50" is 12.50; French keyboards only offer a comma).
 * Money has at most two decimals, so a last separator followed by exactly three digits is a
 * thousands separator ("1,500" and "1.500" are 1500), unless the whole part is 0 ("0,125").
 * Otherwise the last separator is the decimal point and earlier ones are grouping
 * ("1,234.56" and "1.234,56" are 1234.56). Letters and spaces go.
 */
export function cleanMoneyText(text) {
  const s = String(text == null ? '' : text).replace(/[^0-9.,]/g, '');
  const last = Math.max(s.lastIndexOf('.'), s.lastIndexOf(','));
  if (last < 0) return s;
  const whole = s.slice(0, last).replace(/[.,]/g, '');
  const tail = s.slice(last + 1).replace(/[.,]/g, '');
  const grouping = tail.length === 3 && whole !== '' && Number(whole) !== 0;
  return grouping ? `${whole}${tail}` : `${whole}.${tail}`;
}

export function chunkArray(list, size = BATCH_SIZE) {
  const out = [];
  const arr = list || [];
  for (let i = 0; i < arr.length; i += size) out.push(arr.slice(i, i + size));
  return out;
}

// ─── Dates ───────────────────────────────────────────────────────────────────

export function toDayKey(value) {
  if (!value) return '';
  let v = value;
  if (typeof v === 'string') {
    const m = v.match(/^(\d{4})-(\d{2})-(\d{2})/);
    return m ? `${m[1]}-${m[2]}-${m[3]}` : '';
  }
  if (typeof v.toDate === 'function') v = v.toDate();
  if (typeof v === 'number') v = new Date(v);
  if (v && typeof v.getFullYear === 'function' && !Number.isNaN(v.getTime())) {
    return `${v.getFullYear()}-${pad(v.getMonth() + 1)}-${pad(v.getDate())}`;
  }
  return '';
}

function keyToParts(key) {
  const m = String(key || '').match(/^(\d{4})-(\d{2})-(\d{2})$/);
  return m ? [Number(m[1]), Number(m[2]), Number(m[3])] : null;
}

export function addDaysToKey(key, delta) {
  const p = keyToParts(key);
  if (!p) return '';
  const d = new Date(p[0], p[1] - 1, p[2] + delta);
  return `${d.getFullYear()}-${pad(d.getMonth() + 1)}-${pad(d.getDate())}`;
}

/** Inclusive run of YYYY-MM-DD keys. Swapped ends are fixed; bad input gives []. Capped. */
export function dayKeysBetween(startKey, endKey, cap = MAX_SERIES_DAYS) {
  let a = toDayKey(startKey);
  let b = toDayKey(endKey);
  if (!a || !b) return [];
  if (a > b) [a, b] = [b, a];
  const keys = [];
  let cur = a;
  while (cur && cur <= b && keys.length < cap) {
    keys.push(cur);
    cur = addDaysToKey(cur, 1);
  }
  return keys;
}

/** Every day from a tour's first to last scheduled date (a tour has days off too). */
export function tourDayKeys(tour) {
  const dated = ((tour && tour.days) || []).map((d) => toDayKey(d && d.scheduledDate)).filter(Boolean).sort();
  if (!dated.length) return [];
  return dayKeysBetween(dated[0], dated[dated.length - 1]);
}

// ─── Line items ──────────────────────────────────────────────────────────────

/** What one entry is worth. One-off and per-day use the amount; units is count x price. */
export function lineTotal(item) {
  const e = item || {};
  if (e.unitMode === 'units') return num(e.unitCount) * num(e.unitPrice);
  return num(e.amount);
}

export function unitLabel(item) {
  const e = item || {};
  if (e.unitMode === 'units') return `${num(e.unitCount)} units x $${num(e.unitPrice).toFixed(2)}`;
  if (e.unitMode === 'per_day') return 'Per day';
  return 'One-off';
}

export function categoryLabel(id) {
  const hit = EXPENSE_CATEGORIES.find((c) => c.id === id) || INCOME_CATEGORIES.find((c) => c.id === id);
  if (hit) return hit.label;
  // A user's own category id ("sound-gear") reads back as "Sound Gear".
  return String(id || '').split('-').filter(Boolean).map((w) => w[0].toUpperCase() + w.slice(1)).join(' ');
}

export function typeForCategory(id) {
  return INCOME_CATEGORIES.some((c) => c.id === id) ? 'income' : 'expense';
}

/** Older entries used the description as their only title. A merch entry's description is always a note. */
export function entryTitle(entry) {
  const e = entry || {};
  const name = String(e.name || '').trim();
  if (e.type === 'expense' && e.category === 'merch') return name || 'Merch';
  return name || String(e.description || '').trim() || categoryLabel(e.category);
}

export function entryNote(entry) {
  const e = entry || {};
  if (e.type === 'expense' && e.category === 'merch') return String(e.description || '').trim();
  return e.name ? String(e.description || '').trim() : '';
}

export function newSeriesId() {
  return `s-${Date.now().toString(36)}-${Math.random().toString(36).slice(2, 8)}`;
}

/** Which days a new item lands on. Per day uses the tour's days, else the chosen period. */
export function plannedDays({ unitMode, dateKey, rangeStart, rangeEnd, tourDays }) {
  if (unitMode !== 'per_day') return toDayKey(dateKey) ? [toDayKey(dateKey)] : [];
  if (tourDays && tourDays.length) return tourDays.slice(0, MAX_SERIES_DAYS);
  return dayKeysBetween(rangeStart, rangeEnd);
}

/** The entries an edit or delete reaches: this one, all in its series, or this and later days. */
export function scopeTargets(entries, entry, scope) {
  if (!entry) return [];
  if (!entry.seriesId || scope === 'this') return [entry];
  const same = (entries || []).filter((e) => e.seriesId === entry.seriesId);
  if (scope === 'future') return same.filter((e) => String(e.date || '') >= String(entry.date || ''));
  return same;
}

// ─── Subheaders ──────────────────────────────────────────────────────────────

const subId = () => `sh-${Date.now().toString(36)}-${Math.random().toString(36).slice(2, 6)}`;

/** Add a subheader to a category. A name that already exists there is reused, not doubled. */
export function addSubheader(list, category, name) {
  const clean = String(name || '').trim();
  const current = Array.isArray(list) ? list : [];
  if (!clean || !category) return { list: current, id: '' };
  const hit = current.find((s) => s.category === category && s.name.toLowerCase() === clean.toLowerCase());
  if (hit) return { list: current, id: hit.id };
  const id = subId();
  return { list: [...current, { id, category, name: clean }], id };
}

export function renameSubheader(list, id, name) {
  const clean = String(name || '').trim();
  if (!clean) return list;
  return (list || []).map((s) => (s.id === id ? { ...s, name: clean } : s));
}

export function removeSubheader(list, id) {
  return (list || []).filter((s) => s.id !== id);
}

export function subheadersFor(list, category) {
  return (list || []).filter((s) => s.category === category);
}

export function subheaderName(list, id) {
  const hit = (list || []).find((s) => s.id === id);
  return hit ? hit.name : '';
}

/** Fields that move an entry to a category and subheader. The type follows the category. */
export function movePatch(category, subheaderId, entryType) {
  // A user's own category id says nothing about Expense vs Income, so a known entry type wins.
  const type = entryType === 'income' || entryType === 'expense' ? entryType : typeForCategory(category);
  return { category, type, subheader: subheaderId || null };
}

// ─── Totals ──────────────────────────────────────────────────────────────────

/** What one entry puts into INCOME and EXPENSES. Merch adds its band net and its production cost. */
export function entryFlows(entry) {
  const e = entry || {};
  if (e.type === 'expense' && e.category === 'merch') {
    const m = calcMerch(e);
    return { income: m.net, expense: m.production };
  }
  if (e.type === 'income') return { income: num(e.amount), expense: 0 };
  return { income: 0, expense: num(e.amount) };
}

export function overviewTotals(entries) {
  const t = (entries || []).reduce((acc, e) => {
    const f = entryFlows(e);
    acc.income += f.income;
    acc.expenses += f.expense;
    return acc;
  }, { income: 0, expenses: 0 });
  t.net = t.income - t.expenses;
  return t;
}

/** What the form adds up to, and the net once it is saved (an edited entry replaces its old self). */
export function draftTotals(entries, draft, replacedIds) {
  const gone = new Set(replacedIds || []);
  const kept = (entries || []).filter((e) => !gone.has(e.id));
  return { entry: overviewTotals(draft), after: overviewTotals([...kept, ...(draft || [])]) };
}

// ─── Grouping ────────────────────────────────────────────────────────────────

const byDateThenCreated = (a, b) => String(a.date || '').localeCompare(String(b.date || ''));

/** Fold same-series entries into one row; everything else stays a single row. */
export function collapseSeries(items) {
  const rows = [];
  const seen = {};
  [...(items || [])].sort(byDateThenCreated).forEach((e) => {
    if (!e.seriesId) { rows.push({ kind: 'single', key: e.id, first: e, items: [e] }); return; }
    if (seen[e.seriesId]) { seen[e.seriesId].items.push(e); return; }
    const row = { kind: 'series', key: e.seriesId, first: e, items: [e] };
    seen[e.seriesId] = row;
    rows.push(row);
  });
  rows.forEach((r) => {
    r.total = r.items.reduce((sum, e) => sum + (e.category === 'merch' ? entryFlows(e).expense : lineTotal(e)), 0);
    r.income = r.items.reduce((sum, e) => sum + entryFlows(e).income, 0);
    r.expense = r.items.reduce((sum, e) => sum + entryFlows(e).expense, 0);
    r.dayCount = r.items.length;
  });
  return rows;
}

/** "$50.00/day x 3 days", or a plain day count when the days no longer share one amount. */
export function seriesRateLabel(items) {
  const list = items || [];
  const first = list.length ? lineTotal(list[0]) : 0;
  const same = list.every((e) => lineTotal(e) === first);
  const days = `${list.length} ${list.length === 1 ? 'day' : 'days'}`;
  return same ? `$${first.toFixed(2)}/day × ${days}` : `${days} · amounts vary`;
}

/**
 * Entries of one type grouped Category > Subheader. Every category with entries or subheaders is
 * listed; an empty subheader still shows so items can be moved into it. Entries whose subheader was
 * deleted fall back to the category's unsorted group.
 */
export function groupEntries(entries, type, subheaders, extraCategories, keepCategoryIds) {
  const keep = new Set(keepCategoryIds || []);
  const cats = [...(type === 'income' ? INCOME_CATEGORIES : EXPENSE_CATEGORIES), ...(extraCategories || [])];
  return cats.map((cat) => {
    const inCat = (entries || []).filter((e) => e.type === type && e.category === cat.id);
    const subs = subheadersFor(subheaders, cat.id);
    const known = new Set(subs.map((s) => s.id));
    const groups = [];
    const unsorted = inCat.filter((e) => !e.subheader || !known.has(e.subheader));
    if (unsorted.length || !subs.length) groups.push({ id: null, name: '', items: unsorted });
    subs.forEach((s) => groups.push({ id: s.id, name: s.name, items: inCat.filter((e) => e.subheader === s.id) }));
    groups.forEach((g) => {
      g.rows = collapseSeries(g.items);
      g.total = g.rows.reduce((sum, r) => sum + r.total, 0);
      g.income = g.rows.reduce((sum, r) => sum + r.income, 0);
      g.expense = g.rows.reduce((sum, r) => sum + r.expense, 0);
    });
    return {
      id: cat.id, label: cat.label, icon: cat.icon, groups, count: inCat.length,
      total: groups.reduce((s, g) => s + g.total, 0),
      income: groups.reduce((s, g) => s + g.income, 0),
      expense: groups.reduce((s, g) => s + g.expense, 0),
    };
  }).filter((c) => c.count > 0 || subheadersFor(subheaders, c.id).length > 0 || keep.has(c.id));
}

/** Entries by calendar day, oldest first, with each day's income and expenses. */
export function groupByDay(entries) {
  const days = {};
  (entries || []).forEach((e) => {
    const key = toDayKey(e.date) || 'No date';
    if (!days[key]) days[key] = { date: key, items: [], income: 0, expenses: 0 };
    const f = entryFlows(e);
    days[key].items.push(e);
    days[key].income += f.income;
    days[key].expenses += f.expense;
  });
  return Object.values(days).sort((a, b) => a.date.localeCompare(b.date));
}

// ─── Budget revisions (Lauren, 2026-10-21) ─────────────────────────────────────

/**
 * Entries for one tour: 'all' keeps everything, 'none' keeps entries with no tour.
 * With knownTourIds, 'none' also keeps entries whose tour no longer exists, so they never vanish.
 */
export function entriesForTour(entries, tourId, knownTourIds) {
  const list = entries || [];
  if (!tourId || tourId === 'all') return list;
  if (tourId === 'none') {
    const known = knownTourIds ? new Set(knownTourIds) : null;
    return list.filter((e) => !e.linkedTourId || (known && !known.has(e.linkedTourId)));
  }
  return list.filter((e) => e.linkedTourId === tourId);
}

/**
 * The tour Budget opens on: the one running today, else the next to start,
 * else the one that ended most recently, else the first. Tours with no dates come last.
 */
export function pickDefaultTour(tours, today) {
  const list = (tours || []).filter((t) => t && t.id);
  if (!list.length) return '';
  const day = toDayKey(today) || toDayKey(new Date());
  const span = (t) => {
    const keys = (t.dayKeys || []).filter(Boolean).slice().sort();
    return keys.length ? { first: keys[0], last: keys[keys.length - 1] } : null;
  };
  const dated = list.map((t) => ({ t, s: span(t) })).filter((x) => x.s);
  const running = dated.find((x) => x.s.first <= day && x.s.last >= day);
  if (running) return running.t.id;
  const upcoming = dated.filter((x) => x.s.first > day).sort((a, b) => a.s.first.localeCompare(b.s.first));
  if (upcoming.length) return upcoming[0].t.id;
  const past = dated.sort((a, b) => b.s.last.localeCompare(a.s.last));
  if (past.length) return past[0].t.id;
  return list[0].id;
}

/** Dates a new entry starts on for a tour: today while the tour is on, else its first day; the range spans the tour. */
export function tourEntryDates(dayKeys, today) {
  const days = (dayKeys || []).filter(Boolean);
  const t = toDayKey(today) || toDayKey(new Date());
  if (!days.length) return { date: t, start: t, end: t };
  return { date: days.includes(t) ? t : days[0], start: days[0], end: days[days.length - 1] };
}

/** Firestore fields (on users/{uid}) holding a user's own categories. Same names as Renee's Oct 17 version. */
export const CUSTOM_CATEGORY_FIELDS = { expense: 'customExpenseCategories', income: 'customIncomeCategories' };

/** "Sound Gear" -> "sound-gear". Empty when the label has no letters or numbers. */
export function customCategoryId(label) {
  return String(label || '').trim().toLowerCase().replace(/[^a-z0-9]+/g, '-').replace(/^-+|-+$/g, '');
}

/** Keep only well-formed custom categories that don't clash with the built-in ones. */
export function cleanCustomCategories(list) {
  const builtIn = new Set([...EXPENSE_CATEGORIES, ...INCOME_CATEGORIES].map((c) => c.id));
  const seen = new Set();
  return (Array.isArray(list) ? list : []).filter((c) => {
    const id = c && String(c.id || '');
    if (!id || !String(c.label || '').trim() || builtIn.has(id) || seen.has(id)) return false;
    seen.add(id);
    return true;
  }).map((c) => ({ id: String(c.id), label: String(c.label).trim(), icon: 'pricetag-outline' }));
}

/**
 * What deleting a user's own category removes: every entry in it (all tours) and its subheaders.
 * An empty category can go straight away; one with entries needs its name typed (Trev 2026-10-28).
 */
export function categoryDeletePlan(entries, subheaders, type, categoryId) {
  const doomed = (entries || []).filter((e) => e && e.category === categoryId && (e.type || 'expense') === type);
  const subs = (subheaders || []).filter((s) => s && s.category === categoryId);
  return { entries: doomed, subheaders: subs, needsTypedConfirm: doomed.length > 0 };
}

/** The typed confirmation matches the category name, ignoring case and surrounding spaces. */
export function typedNameMatches(typed, name) {
  const a = String(typed || '').trim().toLowerCase();
  return a.length > 0 && a === String(name || '').trim().toLowerCase();
}
